import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import api, { formatApiError } from "@/lib/api";

function ClientPicker({ clients, value, onChange }) {
  const [open, setOpen] = useState(false);
  const picked = clients.find((c) => c.id === value);
  return (
    <div className="flex gap-2 mt-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="flex-1 justify-between font-normal" data-testid="rental-client-picker">
            <span className={picked ? "text-slate-900 truncate" : "text-slate-400"}>{picked ? picked.company_name : "Search clients…"}</span>
            <ChevronsUpDown className="w-4 h-4 text-slate-400 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
          <Command>
            <CommandInput placeholder="Type a client name…" data-testid="rental-client-search" />
            <CommandList className="max-h-60">
              <CommandEmpty>No client found.</CommandEmpty>
              {clients.map((c) => (
                <CommandItem key={c.id} value={`${c.company_name} ${c.id}`} onSelect={() => { onChange(c); setOpen(false); }} data-testid={`rental-client-option-${c.id}`}>
                  <Check className={`w-4 h-4 mr-2 shrink-0 ${value === c.id ? "opacity-100" : "opacity-0"}`} />
                  <div className="min-w-0">
                    <div className="truncate">{c.company_name}</div>
                    {(c.city || c.contact_person) && <div className="text-[11px] text-slate-500 truncate">{[c.city, c.contact_person].filter(Boolean).join(" · ")}</div>}
                  </div>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {picked && (
        <Button type="button" variant="ghost" size="icon" onClick={() => onChange(null)} aria-label="Clear client" data-testid="rental-client-clear"><X className="w-4 h-4" /></Button>
      )}
    </div>
  );
}

export function RentalDialog({ unit, clients, onClose, onSaved }) {
  const [clientId, setClientId] = useState("");
  const [site, setSite] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setClientId(unit?.rental_client_id || "");
    setSite(unit?.rental_site || "");
    setAddresses([]);
  }, [unit]);

  const pickClient = async (c) => {
    setClientId(c?.id || "");
    setAddresses([]);
    if (!c) return;
    const list = [c.billing_address, ...(c.shipping_addresses || []).map((a) => [a.label, a.line].filter(Boolean).join(" — "))].filter(Boolean);
    setAddresses(list);
    setSite(list[0] || "");
  };

  if (!unit) return null;
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/inventory/${unit.id}`, { ...unit, status: "Rental", rental_client_id: clientId, rental_site: site });
      toast.success(`${unit.serial_number} set to Rental`);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!unit} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="rental-dialog">
        <DialogHeader>
          <DialogTitle>{unit.status === "Rental" ? "Rental details" : "Set to Rental"} — {unit.serial_number}</DialogTitle>
          <DialogDescription>Both fields are optional. A unit on Rental can't be booked for demos, reserved or sold as available stock.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Client (optional)</Label>
            <ClientPicker clients={clients} value={clientId} onChange={pickClient} />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Site / location (optional)</Label>
            <textarea value={site} onChange={(e) => setSite(e.target.value)} rows={2} placeholder="Where the unit is installed" className="mt-1 w-full border border-slate-200 rounded-md p-2 text-sm" data-testid="rental-site-input" />
            {addresses.length > 1 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {addresses.map((a, i) => (
                  <button key={i} type="button" onClick={() => setSite(a)} className="text-[11px] px-2 py-0.5 rounded border border-slate-200 text-slate-600 hover:border-fuchsia-300 hover:text-fuchsia-700 max-w-full truncate" data-testid={`rental-site-option-${i}`}>{a.slice(0, 50)}</button>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="rental-save-btn">{saving ? "Saving…" : "Save"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
